/**
 * Maps hand-off URL builders (see CONTEXT.md "Maps hand-off").
 *
 * Every builder searches by "name, address" so the maps app lands on the
 * place's listing, with coordinates only as a location hint. Bare coordinates
 * are used only when there is no address. Pure: platform choice lives in
 * features/business/maps-handoff.ts.
 */

export interface MapsPlace {
  name: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}

function coordinates(place: MapsPlace): string | null {
  return typeof place.latitude === 'number' && typeof place.longitude === 'number'
    ? `${place.latitude},${place.longitude}`
    : null;
}

export function formatAddress(place: MapsPlace): string | null {
  const region = [place.state, place.postalCode].filter(Boolean).join(' ');
  const parts = [place.street, place.city, region].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function searchQuery(place: MapsPlace): string | null {
  const address = formatAddress(place);
  return address ? encodeURIComponent(`${place.name}, ${address}`) : null;
}

export function canHandOff(place: MapsPlace): boolean {
  return formatAddress(place) !== null || coordinates(place) !== null;
}

export function appleMapsUrl(place: MapsPlace): string | null {
  const query = searchQuery(place);
  const ll = coordinates(place);
  if (query) return `https://maps.apple.com/?q=${query}${ll ? `&sll=${ll}` : ''}`;
  if (ll) return `https://maps.apple.com/?q=${encodeURIComponent(place.name)}&ll=${ll}`;
  return null;
}

export function googleMapsWebUrl(place: MapsPlace): string | null {
  const value = searchQuery(place) ?? coordinates(place);
  return value ? `https://www.google.com/maps/search/?api=1&query=${value}` : null;
}

export function googleMapsAppUrl(place: MapsPlace): string | null {
  const query = searchQuery(place);
  const ll = coordinates(place);
  if (query) return `comgooglemaps://?q=${query}${ll ? `&center=${ll}` : ''}`;
  if (ll) return `comgooglemaps://?q=${ll}`;
  return null;
}

export function wazeUrl(place: MapsPlace): string | null {
  const query = searchQuery(place);
  const ll = coordinates(place);
  if (query) return `waze://?q=${query}${ll ? `&ll=${ll}` : ''}`;
  if (ll) return `waze://?ll=${ll}`;
  return null;
}

export function androidGeoUrl(place: MapsPlace): string | null {
  const query = searchQuery(place);
  const ll = coordinates(place);
  if (query) return `geo:${ll ?? '0,0'}?q=${query}`;
  if (ll) return `geo:0,0?q=${ll}(${encodeURIComponent(place.name)})`;
  return null;
}
